import { useQuery } from "@tanstack/react-query";
import { RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { DocumentFrame } from "@/components/user-data-viewer/document-frame";
import { fileHistoryContentQueryOptions } from "@/hooks/use-file-history";
import type { FileHistoryItem } from "@/types/file-history";

interface HistoryDetailSheetProps {
  history: FileHistoryItem | null;
  onClose: () => void;
  onRestore: (history: FileHistoryItem) => void;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("ko-KR");
}

export function HistoryDetailSheet({
  history,
  onClose,
  onRestore,
}: HistoryDetailSheetProps) {
  const query = useQuery(fileHistoryContentQueryOptions(history?._id ?? null));

  return (
    <Sheet
      open={Boolean(history)}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl">
        <SheetHeader className="border-b px-6 py-4">
          <SheetTitle className="truncate">
            {history?.title || "제목 없음"}
          </SheetTitle>
          <SheetDescription>
            {history ? `생성 ${formatDateTime(history.createdAt)}` : ""}
          </SheetDescription>
        </SheetHeader>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {query.isLoading && (
            <div className="space-y-3 p-6">
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          )}
          {query.isError && (
            <p className="p-6 text-sm text-destructive">
              본문을 불러오지 못했습니다.
            </p>
          )}
          {query.data && <DocumentFrame html={query.data.content} />}
        </div>

        <SheetFooter className="border-t p-4">
          <Button
            className="w-full"
            onClick={() => {
              if (history) onRestore(history);
            }}
          >
            <RotateCcw className="mr-2 h-4 w-4" />이 버전으로 복원
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
